import Highcharts from 'highcharts';

import { ThemeMode } from './tokens';

/**
 * Highcharts theming for REMUS dark/light mode.
 *
 * Rather than duplicate the palette, we read the resolved `--ds-*`
 * custom properties off <html> at call time — so the chart chrome
 * always matches whatever `design-system.less` declares for the
 * active theme. Series colours are intentionally left untouched:
 * trend/heatmap series carry data-driven colours, and the default
 * Highcharts qualitative palette reads fine on both surfaces.
 *
 * `applyHighchartsTheme` is idempotent and cheap; call it on every
 * mode change. It both updates the global defaults (for charts not
 * yet created) and live-updates every mounted chart so a toggle is
 * reflected immediately without a remount.
 */

const readVar = (name: string, fallback: string): string => {
  if (typeof window === 'undefined') {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const buildTheme = (mode: ThemeMode): Highcharts.Options => {
  const isDark = mode === 'dark';

  const surface = readVar('--ds-surface', isDark ? '#121d31' : '#ffffff');
  const text = readVar('--ds-text', isDark ? '#e7edf6' : '#0f172a');
  const textSoft = readVar('--ds-text-soft', isDark ? '#c3cee0' : '#334155');
  const muted = readVar('--ds-muted', isDark ? '#8593ab' : '#64748b');
  const border = readVar('--ds-border', isDark ? '#283549' : '#e2e8f0');
  const gridLine = isDark ? readVar('--ds-border-soft', '#1b2740') : readVar('--ds-border', '#e2e8f0');
  const accent = readVar('--ds-accent', isDark ? '#3b82f6' : '#1e3a8a');

  // Tooltip floats above the plot — a slightly raised surface reads best.
  const tooltipBg = isDark ? readVar('--ds-surface-soft', '#18233a') : '#ffffff';

  return {
    chart: {
      backgroundColor: 'transparent',
      plotBackgroundColor: 'transparent',
      style: {
        fontFamily: "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        color: text,
      },
      resetZoomButton: {
        theme: {
          fill: surface,
          stroke: border,
          style: { color: textSoft },
          states: { hover: { fill: surface, style: { color: accent } } },
        },
      },
    },
    title: { style: { color: text } },
    subtitle: { style: { color: muted } },
    xAxis: {
      gridLineColor: gridLine,
      lineColor: border,
      tickColor: border,
      labels: { style: { color: textSoft } },
      title: { style: { color: muted } },
    },
    yAxis: {
      gridLineColor: gridLine,
      lineColor: border,
      tickColor: border,
      labels: { style: { color: textSoft } },
      title: { style: { color: muted } },
    },
    tooltip: {
      backgroundColor: tooltipBg,
      borderColor: border,
      style: { color: text },
    },
    legend: {
      backgroundColor: 'transparent',
      itemStyle: { color: textSoft },
      itemHoverStyle: { color: text },
      itemHiddenStyle: { color: muted },
      title: { style: { color: muted } },
    },
    navigation: {
      buttonOptions: {
        theme: {
          fill: surface,
          stroke: border,
        },
        symbolStroke: textSoft,
      },
      menuStyle: { background: surface, border: `1px solid ${border}` },
      menuItemStyle: { color: text },
      menuItemHoverStyle: { background: accent, color: '#ffffff' },
    },
    plotOptions: {
      series: {
        dataLabels: { color: text, style: { textOutline: 'none' } },
        marker: { lineColor: surface },
      },
      // Boxplot (Measurements statistics) carries no data-driven colour, so the
      // default white fill + light-blue stroke is invisible on a dark surface and
      // washed-out on a light one. Pin every part to design-system tokens so the
      // box, median, whiskers and stems read clearly in both themes.
      boxplot: {
        fillColor: surface,
        color: accent,
        lineWidth: 1,
        medianColor: accent,
        medianWidth: 2,
        stemColor: textSoft,
        stemWidth: 1,
        whiskerColor: textSoft,
        whiskerLength: '70%',
        whiskerWidth: 1,
      },
    },
    navigator: {
      maskFill: isDark ? 'rgba(59, 130, 246, 0.18)' : 'rgba(30, 58, 138, 0.10)',
      outlineColor: border,
      handles: { backgroundColor: surface, borderColor: muted },
      xAxis: { gridLineColor: gridLine, labels: { style: { color: muted } } },
    },
    rangeSelector: {
      buttonTheme: {
        fill: surface,
        stroke: border,
        'stroke-width': 1,
        style: { color: textSoft },
        states: {
          hover: { fill: surface, style: { color: accent } },
          select: { fill: accent, style: { color: '#ffffff' } },
        },
      },
      inputStyle: { color: text, backgroundColor: surface },
      labelStyle: { color: muted },
    },
    scrollbar: {
      barBackgroundColor: border,
      barBorderColor: border,
      buttonBackgroundColor: surface,
      buttonBorderColor: border,
      rifleColor: muted,
      trackBackgroundColor: 'transparent',
      trackBorderColor: border,
    },
    credits: { style: { color: muted } },
    drilldown: {
      activeAxisLabelStyle: { color: accent },
      activeDataLabelStyle: { color: accent },
    },
  };
};

export const applyHighchartsTheme = (mode: ThemeMode): void => {
  const themeOptions = buildTheme(mode);

  // New charts pick this up at construction time.
  Highcharts.setOptions(themeOptions);

  // Live-update charts already on the page so a header toggle is instant.
  (Highcharts.charts || []).forEach(chart => {
    if (!chart) {
      return;
    }
    try {
      chart.update(themeOptions, false);
      chart.redraw(false);
    } catch {
      // A chart mid-teardown can throw on update; ignore — it will be
      // recreated with the new defaults already in place.
    }
  });
};

export default applyHighchartsTheme;
